import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { NavigationDropdown, NavigationItem, NavigationLink } from '../../interfaces/theme/navigation-item.interface';
import { dropdownAnimation } from '../../animations/dropdown.animation';
import { NavigationEnd, Router } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { filter } from 'rxjs/operators';
import { NavigationService } from '../../services/theme/navigation.service';
import icKeyboardArrowRight from '@iconify/icons-ic/twotone-keyboard-arrow-right';
import { AppService } from 'src/@core/services/app.service';
import { isAssignedLeadsOnlyAccount, permissions } from 'src/app/shared/helpers/roles-permissions';

@Component({
  selector: 'codemiro-sidenav-item',
  templateUrl: './sidenav-item.component.html',
  styleUrls: ['./sidenav-item.component.scss'],
  animations: [dropdownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidenavItemComponent implements OnInit, OnChanges, OnDestroy {

  @Input() item: NavigationItem;
  @Input() level: number;
  @Input() collapsed: boolean;
  isOpen: boolean;
  isActive: boolean;
  icKeyboardArrowRight = icKeyboardArrowRight;

  isLink = this.navigationService.isLink;
  isDropdown = this.navigationService.isDropdown;
  isSubheading = this.navigationService.isSubheading;
  profile = this.appService.getCurrentAccount();

  constructor(private router: Router,
    private cd: ChangeDetectorRef,
    private navigationService: NavigationService,
    private appService: AppService) { }

  @HostBinding('class')
  get levelClass() {
    return `item-level-${this.level}`;
  }

  ngOnInit() {
    // Listen to route changes for both links and dropdowns
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      untilDestroyed(this)
    ).subscribe(() => {
      if (this.isDropdown(this.item)) {
        this.onRouteChange();
      } else {
        // For links, trigger change detection to update icon
        this.cd.markForCheck();
      }
    });

    this.navigationService.openChange$.pipe(
      filter(() => this.isDropdown(this.item)),
      untilDestroyed(this)
    ).subscribe(item => this.onOpenChange(item));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.hasOwnProperty('item') && this.isDropdown(this.item)) {
      this.onRouteChange();
    }
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
    this.cd.markForCheck();
  }

  onOpenChange(item: NavigationDropdown) {
    if (this.isChildrenOf(this.item as NavigationDropdown, item)) {
      return;
    }

    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      return;
    }

    if (this.item !== item) {
      this.isOpen = false;
      this.cd.markForCheck();
    }
  }

  onRouteChange() {
    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      this.isActive = true;
      this.isOpen = true;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    } else {
      this.isActive = false;
      this.isOpen = false;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    }
  }

  isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown) {
    if (parent.children.indexOf(item) !== -1) {
      return true;
    }

    return parent.children
      .filter(child => this.isDropdown(child))
      .some(child => this.isChildrenOf(child as NavigationDropdown, item));
  }

  hasActiveChilds(parent: NavigationDropdown) {
    return parent.children.some(child => {
      if (this.isDropdown(child)) {
        return this.hasActiveChilds(child);
      }

      if (this.isLink(child) && !this.isFunction(child.route)) {
        return this.router.isActive(child.route as string, false);
      }
    });
  }

  isFunction(prop: NavigationLink['route']) {
    return prop instanceof Function;
  }

  hideSection(item) {
    // An account that only works assigned leads sees nothing but My Leads.
    // My Leads is identified by the permission that gates it rather than by a
    // role name, so the role behind it can be renamed without hiding the one
    // screen its holders have. Who may see it at all is decided upstream by
    // *showIfPermission; this only keeps the blanket-hide off it.
    const account = this.appService.getCurrentAccount();
    if (this.isAssignedLeadsLink(item)) {
      return false;
    }
    if (isAssignedLeadsOnlyAccount(account)) {
      return true;
    }

    let hideSection = false;
    if (this.profile.roles.includes("AGENCY_ADMIN") || this.profile.roles.includes("AGENCY_STAFF")) {
      if (item.label == 'Bookings' && item && item.permissions && item.permissions.length) {
        if (this.profile && this.profile.entity.id && this.profile.user.email == 'hour4u.anmol@gmail.com') {
          item.permissions.forEach(element => {
            if (element == permissions.BOOKINGS_CREATE) {
              hideSection = false;
            }
          });
        }
        else {
          hideSection = true;
        }
      } else {
        hideSection = false;
      }
    } else {
      hideSection = false;
    }
    return hideSection;
  }

  /** The My Leads entry — the one link that outlives the blanket-hide above. */
  private isAssignedLeadsLink(item): boolean {
    return !!(item && item.permissions && item.permissions.indexOf(permissions.MY_LEADS_VIEW) !== -1);
  }

  /**
   * Returns iconActive if item is active and iconActive is provided, otherwise returns icon
   */
  getIcon(item: NavigationItem, isActive: boolean = false): string {
    if (!item || !('icon' in item)) {
      return null;
    }
    
    if ('iconActive' in item && item.iconActive && isActive) {
      return item.iconActive;
    }
    
    // Otherwise return the regular icon
    return item.icon;
  }

  /**
   * Check if the item is currently active
   */
  isItemActive(item: NavigationItem): boolean {
    if (this.isLink(item) && !this.isFunction(item.route)) {
      const route = item.route as string;
      const url = this.router.url.split('?')[0].split('#')[0];

      if (route === '/job') {
        return url === '/job' || url === '/job/';
      }
      if (route === '/job/tasks') {
        return url === '/job/tasks' || url === '/tasks';
      }
      if (route === '/job/outcomes') {
        return url === '/job/outcomes' || url === '/outcomes';
      }

      const exact = item.routerLinkActive && item.routerLinkActive.exact;
      return this.router.isActive(route, !!exact);
    }
    if (this.isDropdown(item)) {
      return this.isActive || this.hasActiveChilds(item as NavigationDropdown);
    }
    return false;
  }

  ngOnDestroy(): void { }
}
